import {
  type ApplicationConfig,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { routes } from './app.routes';
import { ErrorStateMatcher, MAT_DATE_LOCALE } from '@angular/material/core';
import { hu } from 'date-fns/locale';
import { MAT_DATE_FNS_FORMATS, provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { client } from '../backend/main/client.gen';
import { client as fileClient } from '../backend/file/client.gen';
import { provideHeyApiClient } from '../backend/main/client/client.gen';
import { provideHeyApiClient as provideHeyApiFileClient } from '../backend/file/client/client.gen';
import { appInitializerPreloadScopes, translocoProvider } from '@pct/ng-transloco';
import { environment } from '../environment';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { provideToastr } from 'ngx-toastr';
import { AuthService } from './auth/auth.service';
import { AUTH_SERVICE_INJECTION_TOKEN } from './auth/auth-service-injection-token';
import { provideKeycloakAngular } from './auth/keycloak.config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAIN_MENU_CONFIG_TOKEN } from './layout/main-menu-config.token';
import { MAIN_MENU_CONFIG } from './app-main-menu.config';
import { TOP_RIGHT_MENU_CONFIG_TOKEN } from './layout/top-right-menu-config.token';
import { TOP_RIGHT_MENU_CONFIG } from './top-right-menu.config';
import { provideStore } from '@ngxs/store';
import { AppState } from '@pct/ng-feature-app-layout-state';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { httpErrorInterceptor } from './common/error/http-error.interceptor';
import { provideErrorHandlers } from './common/error/provide-error-handlers';
import { UniversalErrorHandler } from './common/error/handlers/universal-error-handler';
import { UnauthorizedErrorHandler } from './common/error/handlers/unauthorized-error-handler';
import { ServerErrorHandler } from './common/error/handlers/server-error-handler';
import { includeBearerTokenInterceptor } from 'keycloak-angular';
import { TableState } from '@pct/ng-feature-table';
import { GALLERY_IMAGE_CONFIG_INJECTION_TOKEN } from './common/gallery/gallery-control/config-injection-token/gallery-image-config-injection-token';
import { COVER_IMAGE_CONFIG_INJECTION_TOKEN } from './common/cover-image/config-injection-token/cover-image-config-injection-token';
import { FileService } from './common/file-service/file.service';
import { FILE_UPLOAD_SERVICE_TOKEN } from './common/file-upload/native/service/abstract-file-upload.service';
import { BehaviourConstants } from './constants/behaviour.constants';
import { CustomErrorStateMatcher } from '@pct/ng-form';
import { I18nProviderInjectionToken } from '@pct/ng-i18n';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),

    provideKeycloakAngular(),
    provideHttpClient(
      withFetch(),
      withInterceptors([includeBearerTokenInterceptor, httpErrorInterceptor]),
    ),
    provideErrorHandlers([UnauthorizedErrorHandler, ServerErrorHandler, UniversalErrorHandler]),

    provideStore(
      [AppState, TableState],
      { developmentMode: environment.isAngularDevMode },
      ...(environment.isAngularDevMode ? [withNgxsReduxDevtoolsPlugin()] : []),
    ),
    {
      provide: AUTH_SERVICE_INJECTION_TOKEN,
      useExisting: AuthService,
    },
    provideHeyApiClient(client),
    provideHeyApiFileClient(fileClient),
    { provide: ErrorStateMatcher, useClass: CustomErrorStateMatcher },
    provideToastr({
      tapToDismiss: true,
      closeButton: true,
      preventDuplicates: true,
      countDuplicates: true,
      resetTimeoutOnDuplicate: true,
      includeTitleDuplicates: true,
      newestOnTop: true,
      progressBar: true,
      progressAnimation: 'decreasing',
      timeOut: 1000 * 10,
      positionClass: 'toast-top-right',
    }),
    {
      provide: LOCALE_ID,
      useValue: 'hu-HU',
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: hu,
    },
    provideDateFnsAdapter({
      ...MAT_DATE_FNS_FORMATS,
      display: {
        ...MAT_DATE_FNS_FORMATS.display,
        dateInput: 'P  HH:mm',
      },
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),

    appInitializerPreloadScopes('hu', I18nProviderInjectionToken.scope),
    translocoProvider(!environment.isAngularDevMode),
    provideTranslocoMessageformat(),
    {
      provide: MAIN_MENU_CONFIG_TOKEN,
      useValue: MAIN_MENU_CONFIG,
    },
    {
      provide: TOP_RIGHT_MENU_CONFIG_TOKEN,
      useValue: TOP_RIGHT_MENU_CONFIG,
    },

    {
      provide: COVER_IMAGE_CONFIG_INJECTION_TOKEN,
      useValue: {
        ...BehaviourConstants.DEFAULT_UPLOAD_CONFIG,
        cropConfig: {
          showAspectRatio: false,
          defaultAspectRatio: '4:3',
          showMaintainAspectRatio: false,
          maintainAspectRatioDefaultValue: true,
          showContainWithinAspectRatio: false,
          containWithinAspectRatioDefaultValue: true,
          showRotate: false,
        },
      },
    },
    {
      provide: GALLERY_IMAGE_CONFIG_INJECTION_TOKEN,
      useValue: {
        ...BehaviourConstants.DEFAULT_UPLOAD_CONFIG,
        overwriteNewUpload: false,
        multiple: true,
        multipleUpload: false,
        maxFiles: 4,
        cropConfig: {
          showAspectRatio: false,
          defaultAspectRatio: '4:3',
          showMaintainAspectRatio: false,
          maintainAspectRatioDefaultValue: true,
          showContainWithinAspectRatio: false,
          containWithinAspectRatioDefaultValue: true,
          showRotate: false,
        },
      },
    },
    {
      provide: FILE_UPLOAD_SERVICE_TOKEN,
      useClass: FileService,
    },
  ],
};
